import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Polygon } from '@react-google-maps/api';

import { Colors } from 'utils/constants';
import debounce from 'utils/debounce';

import { Coordinates, PolygonGeometry } from 'interfaces/vector.interface';
import { PolygonValidationStatus } from 'interfaces/awd.interface';

import {
	setEditablePolygons,
	setIsSelectedPolygonSaved,
	setSelectedPolygonValidationStatus,
} from 'reducers/awdView.reducer';
import { useSearchParams } from 'customHooks/useSearchParams';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { updateSelectedPolygonIndex } from 'modules/AwdMapView';
import MarkerUnit from '../MarkerUnit';

type PolygonType = 'PRIMARY' | 'SECONDARY';

type PolygonWithMarkersProps = {
	borderStoke: string;
	colorIndex: number;
	fillColor: string;
	isInteractive?: boolean;
	polygonIndex: number;
	paths: PolygonGeometry;
	polygonType?: PolygonType;
	validateSelectedPolygon: (
		a: google.maps.Polygon,
		b: number,
	) => PolygonValidationStatus;
};

const hasParentPoint = false;

const PolygonWithMarkers: React.FC<PolygonWithMarkersProps> = ({
	borderStoke,
	colorIndex,
	fillColor,
	isInteractive = false,
	polygonIndex,
	paths,
	polygonType = 'SECONDARY',
	validateSelectedPolygon,
}): JSX.Element => {
	const dispatch = useAppDispatch();
	const searchParams = useSearchParams();
	const editablePolygons = useAppSelector((s) => s.awdView.editablePolygons);
	const selectedPolygonIndex = useAppSelector(
		(s) => s.awdView.selectedPolygonIndex,
	);
	const isSelectedPolygonSaved = useAppSelector(
		(s) => s.awdView.isSelectedPolygonSaved,
	);
	const isSelectedPolygonEditable = useAppSelector(
		(s) => s.awdView.isSelectedPolygonEditable,
	);
	const polygonDrawingStage = useAppSelector(
		(s) => s.awdView.polygonDrawingStage,
	);
	const pointsOfFarmId = useAppSelector((s) => s.awdView.pointsOfFarmId);

	const polygonRef: any = useRef(null);
	const listenersRef: any = useRef([]);
	const prevVertexRef: any = useRef(null);
	const counterRef: any = useRef(0);
	const timerRef: any = useRef(null);

	const [isEditable, setIsEditable] = useState(false);
	const [isPolygonMoving, setIsPolygonMoving] = useState<boolean>(false);

	const getPolygonOptions = useCallback(
		() => ({
			fillColor: fillColor || Colors[colorIndex % 11],
			fillOpacity: fillColor ? 1 : 0,
			strokeColor:
				borderStoke && borderStoke !== '' ? borderStoke : '#FADDFF',
			strokeOpacity: 1,
			strokeWeight: 3,
			clickable:
				isInteractive &&
				// selectedPolygonIndex === polygonIndex &&
				// isSelectedPolygonEditable &&
				// isEditable,
				((selectedPolygonIndex === polygonIndex &&
					isSelectedPolygonEditable &&
					isEditable) ||
					!hasParentPoint),
			draggable:
				polygonType === 'PRIMARY' &&
				isInteractive &&
				isSelectedPolygonEditable &&
				selectedPolygonIndex === polygonIndex &&
				isEditable,
			editable:
				isInteractive &&
				selectedPolygonIndex === polygonIndex &&
				isSelectedPolygonEditable &&
				isEditable,
			geodesic: false,
			// suppressUndo: false,
			zIndex: 2,
		}),
		[
			fillColor,
			colorIndex,
			borderStoke,
			isInteractive,
			polygonType,
			selectedPolygonIndex,
			polygonIndex,
			isEditable,
			isSelectedPolygonEditable,
		],
	);

	const resetRefs = useCallback(() => {
		prevVertexRef.current = null;
		counterRef.current = 0;
		timerRef.current = null;
	}, []);

	const deleteVertexOnDoubleClick = useCallback(
		(
			currentPath: PolygonGeometry,
			mouseBtnClicked: number,
			vertexIndex: number,
		) => {
			clearTimeout(timerRef.current);
			// return if mouse btn other than left btn is clicked
			if (mouseBtnClicked > 1) {
				resetRefs();
				return;
			}
			timerRef.current = setTimeout(() => {
				counterRef.current = 0;
			}, 1000);
			if (vertexIndex !== prevVertexRef.current) {
				// vertex changed
				prevVertexRef.current = vertexIndex;
				counterRef.current = 1;
			} else {
				counterRef.current += 1;
				if (counterRef.current === 2) {
					clearTimeout(timerRef.current);
					resetRefs();
					currentPath.splice(vertexIndex, 1);
				}
			}
		},
		[resetRefs],
	);

	const onEdit = useCallback(
		(e) => {
			if (
				!isInteractive ||
				['PRE_START', 'SAVED', 'START'].includes(polygonDrawingStage)
			)
				return;
			if (
				polygonRef.current &&
				editablePolygons !== undefined &&
				Object.keys(editablePolygons).length > 0
			) {
				const polygons = JSON.parse(JSON.stringify(editablePolygons));
				const nextPath = polygonRef.current
					.getPath()
					.getArray()
					.map((latLng: any) => {
						return { lat: latLng.lat(), lng: latLng.lng() };
					});
				if (e?.vertex !== undefined && nextPath.length > 3) {
					deleteVertexOnDoubleClick(
						nextPath,
						e.domEvent.which,
						e.vertex,
					);
				}
				polygons[`${polygonIndex}`].geometry = nextPath;
				dispatch(setEditablePolygons({ ...polygons }));
				dispatch(setIsSelectedPolygonSaved(false));
			}
		},
		[
			isInteractive,
			polygonDrawingStage,
			editablePolygons,
			polygonIndex,
			dispatch,
			deleteVertexOnDoubleClick,
		],
	);

	const handleClick = useCallback(() => {
		if (!isInteractive) return;
		updateSelectedPolygonIndex({
			dispatch,
			editablePolygons,
			isSelectedPolygonSaved,
			polygonIndex,
			pointsOfFarmId,
			searchParams,
			selectedPolygonIndex,
		});
	}, [
		isInteractive,
		editablePolygons,
		polygonIndex,
		dispatch,
		isSelectedPolygonSaved,
		pointsOfFarmId,
		searchParams,
		selectedPolygonIndex,
	]);

	const toggleMarkersOnDrag = (dragStart: boolean) => {
		if (!isInteractive) return;
		setIsPolygonMoving && setIsPolygonMoving(dragStart);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleDragEvent = debounce((e: google.maps.MapMouseEvent) => {
		if (!isInteractive) return;
		onEdit(e);
	}, 500);

	const onUnmount = useCallback(() => {
		listenersRef.current.forEach((lis: any) => lis.remove());
		polygonRef.current = null;
	}, []);

	const onLoad = (polygon: google.maps.Polygon) => {
		polygonRef.current = polygon;
		const path = polygon.getPath();
		listenersRef.current.push(
			path.addListener('set_at', onEdit),
			path.addListener('insert_at', onEdit),
			path.addListener('remove_at', onEdit),
		);
		// const nextPath = polygonRef.current;
		// if (selectedPoint !== undefined && selectedPolygonIndex !== undefined) {
		// 	editablePolygons[
		// 		selectedPolygonIndex
		// 	].parentPointSignatures?.forEach((parentPointSignature) => {
		// 		dispatch(
		// 			updatePointData({
		// 				pointIdentifier: parentPointSignature,
		// 				payload: {
		// 					geofencedAcres:
		// 						google.maps.geometry.spherical.computeArea(
		// 							path,
		// 						) * AREA_CONVERSION_FACTOR,
		// 				},
		// 			}),
		// 		);
		// 	});
		// }
	};

	useEffect(() => {
		if (
			!polygonRef ||
			polygonRef.current === null ||
			selectedPolygonIndex !== polygonIndex
		)
			return;
		dispatch(
			setSelectedPolygonValidationStatus(
				validateSelectedPolygon(polygonRef.current, polygonIndex),
			),
		);
	}, [
		dispatch,
		paths,
		polygonIndex,
		selectedPolygonIndex,
		validateSelectedPolygon,
	]);

	useEffect(() => {
		if (![polygonIndex, undefined].includes(selectedPolygonIndex)) {
			setIsEditable(false);
		} else if (
			polygonIndex === selectedPolygonIndex &&
			!isSelectedPolygonSaved
		) {
			setIsEditable(true);
		} else {
			setIsEditable(false);
		}
		return () => {
			resetRefs();
		};
	}, [isSelectedPolygonSaved, polygonIndex, resetRefs, selectedPolygonIndex]);

	return (
		<>
			<Polygon
				options={getPolygonOptions()}
				onClick={handleClick}
				// onRightClick={(e) => handleRightClick(e)}
				// onDblClick={(e) => handleDblClick(e)}
				onMouseUp={(e) => onEdit(e)}
				onDrag={(e) => handleDragEvent(e)}
				onDragEnd={() => toggleMarkersOnDrag(false)}
				onDragStart={() => toggleMarkersOnDrag(true)}
				onLoad={onLoad}
				onUnmount={onUnmount}
				paths={paths}
			/>
			{!isPolygonMoving &&
				selectedPolygonIndex === polygonIndex &&
				paths?.length > 0 &&
				paths.map((coords: Coordinates, index: number) => (
					<MarkerUnit
						key={`${coords.lat}-${coords.lng}-${index}`}
						position={coords}
						zoom={19}
					/>
				))}
		</>
	);
};

export default React.memo(PolygonWithMarkers);
